import { useEffect, useState, ReactNode } from 'react';

import Helmet from 'react-helmet';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';

import FabricAnalyticsListeners, {
  FabricChannel,
} from '@atlaskit/analytics-listeners';
import AppProvider from '@atlaskit/app-provider';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { SpotlightManager } from '@atlaskit/onboarding';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { Provider as ConnectProvider } from '@atlassian/bitbucket-connect-react';
import { AnalyticsClientManager } from '@atlassian/bitkit-analytics';
import { ExperienceTrackerContext } from '@atlassian/experience-tracker';
import CrossFlowProvider from '@atlassiansox/cross-flow-support/bitbucket';

import {
  DevToolsProvider,
  LazyDevToolsDialog,
} from 'src/components/dev-tools-loader';
import { AppErrorBoundary } from 'src/components/error-boundary';
import Favicon from 'src/components/favicon';
import { Heartbeat } from 'src/components/heartbeat/heartbeat';
import KeyboardShortcutDialog from 'src/components/keyboard-shortcut-dialog';
import { ScreenSpaceFlagsPlacement } from 'src/components/post-office/screen-space-flags-placement';
import {
  BitbucketCustomData,
  useReactUFOWatcher,
} from 'src/components/react-ufo';
import { LoadRecentWorkspaces } from 'src/components/recent-workspace-loader';
import StatuspagePoller from 'src/components/statuspage-poller';
import { InitializeWebStorageControls } from 'src/components/storage-controls';
import { AddonManager, ConnectHost, sendAnalyticsEvent } from 'src/connect';
import { DEFAULT_TITLE, TITLE_TEMPLATE } from 'src/constants/helmet';
import { EngagementProvider } from 'src/contexts/engagement-provider';
import { ForgeAppsProvider } from 'src/contexts/forge-apps-provider';
import { PostOfficeContextProvider } from 'src/contexts/post-office-context-provider';
import { RecentDataProvider } from 'src/contexts/recent-data';
import { StatsigFeatureProvider } from 'src/contexts/statsig';
import { StatsigFeatureKeys } from 'src/features';
import { ApolloProvider } from 'src/graphql/apollo-provider';
import {
  getRouteFeaturesFromAppData,
  getRoutes,
  history,
  Router as ResourceRouter,
  AppProviderLink,
} from 'src/router';
import GlobalCreateBranchDialog from 'src/sections/create-branch/containers/global-dialog';
import TermsAndConditionsModal from 'src/sections/global/components/terms-and-conditions-modal';
import { UnlinkedWorkspaceFeaturelistMsg } from 'src/sections/global/components/unlinked-workspace-featurelist-msg';
import UnlinkedWorkspaceNotifications from 'src/sections/global/components/unlinked-workspace-notifications';
import { Flags } from 'src/sections/global/containers/flags';
import MarketingConsentManager from 'src/sections/global/containers/marketing-consent-manager';
import SiteMessageFlagManager from 'src/sections/global/containers/site-message-flag-manager';
import { AnalyticsBridgeListener } from 'src/utils/analytics/analytics-bridge';
import { analyticsClient } from 'src/utils/analytics/client';
import { getExperienceTracker } from 'src/utils/analytics/experience-tracker';
import { captureExceptionWithTags } from 'src/utils/sentry';

import { useBrowserMetricsWatcher } from '../components/browser-metrics';
import { getContainersPlugin, getUsersPlugin } from '../growth/cross-flow';
import locale from '../locale';
import { settings } from '../settings';
import { SpaComponentProps } from '../ssr/common/types';
import { bitbucketStore as ourStore } from '../store';

import { CurrentDataContext, CurrentDataProvider } from './current-data';
import { AppDataProvider } from './data';
import { NavDataProvider } from './nav-data';
import { UniversalNavigation } from './navigation';
import {
  getDefaultMessages,
  getPlatformMessages,
  LocalizationMessages,
} from './platform-messages';

const EXCLUDED_CHANNELS = [
  FabricChannel.atlaskit,
  FabricChannel.elements,
  FabricChannel.editor,
];

// SpaComponentProps are provided in SSR
type RootProps = Partial<SpaComponentProps>;

const Nav4AppProvider = (props: { children: ReactNode }) => {
  return (
    <AppErrorBoundary>
      <AppProvider routerLinkComponent={AppProviderLink}>
        {props.children}
      </AppProvider>
    </AppErrorBoundary>
  );
};

const Root = ({
  appSettings = settings(),
  isSsr = false,
  location = '',
  appData = {
    features: {},
    frontbucket_environment: 'local',
    frontbucket_version: 'local',
    links: {
      overviewUrl: '',
      backButtonUrl: undefined,
    },
    initialContext: {},
  },
}: RootProps) => {
  useBrowserMetricsWatcher();
  useReactUFOWatcher();
  const routeFeatures = getRouteFeaturesFromAppData(appData);

  const routes = getRoutes(routeFeatures);

  const [platformMessages, setPlatformMessages] =
    useState<LocalizationMessages>({});

  useEffect(() => {
    const fetchPlatformMessages = async () => {
      try {
        const translations = await Promise.all(
          getPlatformMessages(locale.locale)
        );
        const allTranslations = translations.reduce((acc, translation) => {
          const defaults = getDefaultMessages(translation);
          // Only merge the keys with string values from the default messages
          // See https://ops.internal.atlassian.com/jira/browse/HOT-84998
          const filteredDefaults = Object.fromEntries(
            Object.entries(defaults).filter(
              ([_key, value]) => typeof value === 'string'
            )
          );
          return { ...acc, ...filteredDefaults };
        }, {});
        setPlatformMessages(allTranslations);
      } catch (e) {
        captureExceptionWithTags(e);
      }
    };
    fetchPlatformMessages();
  }, []);

  // locale.messages is empty due to transifex deactivation. We can safely keep
  // this here until traduki translations are added.
  const localeMessages: Record<string, string> = {};
  Object.entries(locale.messages || {}).forEach(
    ([key, message]: [string, string]) => {
      localeMessages[key.replace(/\\/g, '')] = message;
    }
  );

  // Locale messages will contain translated BBC strings.
  // Platform messages are included from the platform package distributions.
  const combinedMessages = {
    ...localeMessages,
    ...platformMessages,
  };

  return (
    <UFOSegment name="bitbucket-root">
      <IntlProvider
        locale={locale.locale}
        defaultLocale="en"
        messages={combinedMessages}
      >
        <AppDataProvider appData={appData}>
          <CurrentDataProvider initialContext={appData.initialContext}>
            <CurrentDataContext.Consumer>
              {currentData => (
                <AnalyticsClientManager
                  analyticsClient={analyticsClient()}
                  accountId={appData.user?.aaid || undefined}
                  workspaceId={currentData.workspace?.uuid || appData.tenantId}
                />
              )}
            </CurrentDataContext.Consumer>
            <StatsigFeatureProvider>
              <InitializeWebStorageControls />
              <DevToolsProvider>
                <NavDataProvider>
                  <ApolloProvider
                    frontbucketVersion={appData.frontbucket_version}
                  >
                    <CrossFlowProvider
                      analyticsClient={analyticsClient()}
                      locale={locale.locale}
                      {...(!FeatureGates.checkGate(
                        StatsigFeatureKeys.removeXflowPluginsFromBitbucket
                      ) && {
                        plugins: [getContainersPlugin, getUsersPlugin],
                      })}
                    >
                      <SmartCardProvider>
                        <Provider store={ourStore}>
                          <PostOfficeContextProvider>
                            <ScreenSpaceFlagsPlacement />
                            <LoadRecentWorkspaces />
                            <ConnectProvider
                              connectHost={ConnectHost}
                              addonManager={AddonManager}
                              analyticsEventHandler={sendAnalyticsEvent}
                            >
                              <FabricAnalyticsListeners
                                client={analyticsClient()}
                                excludedChannels={EXCLUDED_CHANNELS}
                              >
                                <AnalyticsBridgeListener>
                                  <SpotlightManager>
                                    <ExperienceTrackerContext.Provider
                                      value={getExperienceTracker()}
                                    >
                                      <Nav4AppProvider>
                                        <RecentDataProvider>
                                          <ResourceRouter
                                            history={history}
                                            routes={routes}
                                            resourceContext={{
                                              reduxStore: ourStore,
                                              appSettings,
                                              appData,
                                            }}
                                            isSsr={isSsr}
                                            location={location}
                                          >
                                            <EngagementProvider>
                                              <ForgeAppsProvider>
                                                <BitbucketCustomData />
                                                <KeyboardShortcutDialog />
                                                <Favicon />
                                                <StatuspagePoller />
                                                <Flags />
                                                <UnlinkedWorkspaceNotifications />
                                                <UnlinkedWorkspaceFeaturelistMsg />
                                                <Helmet
                                                  defaultTitle={DEFAULT_TITLE}
                                                  titleTemplate={TITLE_TEMPLATE}
                                                />
                                                <MarketingConsentManager />
                                                <SiteMessageFlagManager />
                                                <TermsAndConditionsModal />
                                                {
                                                  // @ts-ignore
                                                  <GlobalCreateBranchDialog />
                                                }
                                                <UniversalNavigation />
                                                <Heartbeat />
                                                <LazyDevToolsDialog />
                                              </ForgeAppsProvider>
                                            </EngagementProvider>
                                          </ResourceRouter>
                                        </RecentDataProvider>
                                      </Nav4AppProvider>
                                    </ExperienceTrackerContext.Provider>
                                  </SpotlightManager>
                                </AnalyticsBridgeListener>
                              </FabricAnalyticsListeners>
                            </ConnectProvider>
                          </PostOfficeContextProvider>
                        </Provider>
                      </SmartCardProvider>
                    </CrossFlowProvider>
                  </ApolloProvider>
                </NavDataProvider>
              </DevToolsProvider>
            </StatsigFeatureProvider>
          </CurrentDataProvider>
        </AppDataProvider>
      </IntlProvider>
    </UFOSegment>
  );
};

// eslint-disable-next-line import/no-default-export
export default Root;
